import { useState, useEffect } from 'react';
import {
  Filter,
  X,
  RefreshCw,
  Save,
  ChevronDown,
  ChevronUp,
  Calendar,
  Building2,
  Package,
  TrendingUp,
  MapPin,
  List
} from 'lucide-react';
import { SmartDropdown, useToast } from '../ui';
import useAnalyticsStore from '../../stores/analyticsStore';
import { createSavedView, getSavedViews } from '../../services/analyticsService';

const SliceAndDicePanel = ({ onApply }) => {
  const { success, error: showError } = useToast();

  // Zustand store
  const {
    filters,
    appliedFilters,
    showFilters,
    activeTab,
    updateFilters,
    applyFilters,
    resetFilters,
    toggleFilters,
    setShowFilters,
    hasUnappliedFilters,
    savedViews,
    setSavedViews,
    activeSavedView,
    setActiveSavedView
  } = useAnalyticsStore();

  // Local state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [viewName, setViewName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Load saved views on mount
  useEffect(() => {
    loadSavedViews();
  }, [activeTab]);

  const loadSavedViews = async () => {
    try {
      const views = await getSavedViews({ tab: activeTab });
      setSavedViews(views);
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
  };

  const handleApplyFilters = () => {
    applyFilters();
    if (onApply) {
      onApply(filters);
    }
    success('Filters applied successfully!');
  };

  const handleResetFilters = () => {
    resetFilters();
    setActiveSavedView(null);
    success('Filters reset successfully!');
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) {
      showError('Please enter a view name');
      return;
    }

    setSavingView(true);
    try {
      const viewData = {
        name: viewName,
        description: `Saved view for ${activeTab} tab`,
        filter_config: filters,
        tab: activeTab,
        chart_config: {},
        is_shared: false
      };

      await createSavedView(viewData);
      await loadSavedViews();
      setViewName('');
      setShowSaveDialog(false);
      success('View saved successfully!');
    } catch (err) {
      showError('Failed to save view: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingView(false);
    }
  };

  const handleLoadView = (viewId) => {
    const view = savedViews.find((v) => v.id === parseInt(viewId));
    if (view) {
      setActiveSavedView(view);
      success(`Loaded view: ${view.name}`);
    }
  };

  const removeFilterChip = (category, value) => {
    const updated = { ...filters };
    if (Array.isArray(updated[category])) {
      updated[category] = updated[category].filter((v) => v !== value);
    }
    updateFilters(updated);
  };

  if (!showFilters) {
    return null;
  }

  const hasAppliedFilters =
    appliedFilters.operations?.length > 0 ||
    appliedFilters.suppliers?.length > 0 ||
    appliedFilters.materials?.length > 0 ||
    appliedFilters.locations?.length > 0 ||
    appliedFilters.stages?.length > 0 ||
    appliedFilters.status?.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Panel Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Filter className="h-5 w-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Slice & Dice Filters</h2>
          {hasUnappliedFilters() && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Unapplied Changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Saved Views Dropdown */}
          {savedViews.length > 0 && (
            <SmartDropdown
              value={activeSavedView?.id?.toString() || ''}
              onChange={handleLoadView}
              options={[
                { value: '', label: 'Select Saved View' },
                ...savedViews.map((view) => ({
                  value: view.id.toString(),
                  label: view.name
                }))
              ]}
              className="w-48"
            />
          )}

          <button
            onClick={() => setShowFilters(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="p-4">
        {/* Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Time Range */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 mr-2 text-gray-500" />
              Time Range
            </label>
            <SmartDropdown
              value={filters.timeRange?.period || '30'}
              onChange={(value) =>
                updateFilters({
                  timeRange: { ...filters.timeRange, period: value }
                })
              }
              options={[
                { value: '7', label: 'Last 7 Days' },
                { value: '14', label: 'Last 14 Days' },
                { value: '30', label: 'Last 30 Days' },
                { value: '60', label: 'Last 60 Days' },
                { value: '90', label: 'Last 90 Days' },
                { value: 'custom', label: 'Custom Range' }
              ]}
            />
          </div>

          {/* Operations */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <List className="h-4 w-4 mr-2 text-gray-500" />
              Operations
            </label>
            <SmartDropdown
              value=""
              onChange={(value) => {
                if (value && !filters.operations?.includes(value)) {
                  updateFilters({
                    operations: [...(filters.operations || []), value]
                  });
                }
              }}
              options={[
                { value: '', label: 'Select Operation' },
                { value: 'gate_check', label: 'Gate Check' },
                { value: 'document_verification', label: 'Document Verification' },
                { value: 'dock_assignment', label: 'Dock Assignment' },
                { value: 'unloading', label: 'Unloading' },
                { value: 'qc_inspection', label: 'QC Inspection' },
                { value: 'grn_creation', label: 'GRN Creation' }
              ]}
              placeholder="All Operations"
            />
          </div>

          {/* Suppliers */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Building2 className="h-4 w-4 mr-2 text-gray-500" />
              Suppliers
            </label>
            <SmartDropdown
              value=""
              onChange={(value) => {
                if (value && !filters.suppliers?.includes(value)) {
                  updateFilters({
                    suppliers: [...(filters.suppliers || []), value]
                  });
                }
              }}
              options={[
                { value: '', label: 'Select Supplier' },
                { value: 'supplier1', label: 'Supplier 1' },
                { value: 'supplier2', label: 'Supplier 2' }
              ]}
              placeholder="All Suppliers"
            />
          </div>

          {/* Materials */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Package className="h-4 w-4 mr-2 text-gray-500" />
              Materials
            </label>
            <SmartDropdown
              value=""
              onChange={(value) => {
                if (value && !filters.materials?.includes(value)) {
                  updateFilters({
                    materials: [...(filters.materials || []), value]
                  });
                }
              }}
              options={[
                { value: '', label: 'Select Material' },
                { value: 'material1', label: 'Material 1' },
                { value: 'material2', label: 'Material 2' }
              ]}
              placeholder="All Materials"
            />
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 mb-3"
        >
          {showAdvanced ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Advanced Filters
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Show Advanced Filters
            </>
          )}
        </button>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            {/* Locations */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                Locations
              </label>
              <SmartDropdown
                value=""
                onChange={(value) => {
                  if (value && !filters.locations?.includes(value)) {
                    updateFilters({
                      locations: [...(filters.locations || []), value]
                    });
                  }
                }}
                options={[
                  { value: '', label: 'Select Location' },
                  { value: 'warehouse1', label: 'Warehouse 1' },
                  { value: 'warehouse2', label: 'Warehouse 2' }
                ]}
                placeholder="All Locations"
              />
            </div>

            {/* Performance Range */}
            <div className="col-span-2">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <TrendingUp className="h-4 w-4 mr-2 text-gray-500" />
                Performance Efficiency Range
              </label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.performance?.minEfficiency || 0}
                    onChange={(e) =>
                      updateFilters({
                        performance: {
                          ...filters.performance,
                          minEfficiency: parseInt(e.target.value) || 0
                        }
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Min %"
                  />
                </div>
                <span className="text-gray-500">to</span>
                <div className="flex-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.performance?.maxEfficiency || 100}
                    onChange={(e) =>
                      updateFilters({
                        performance: {
                          ...filters.performance,
                          maxEfficiency: parseInt(e.target.value) || 100
                        }
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Max %"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Applied Filters Display */}
        {hasAppliedFilters && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-blue-900">Active Filters:</span>
              {appliedFilters.operations?.map((op) => (
                <FilterChip
                  key={op}
                  label={op.replace('_', ' ')}
                  onRemove={() => removeFilterChip('operations', op)}
                  color="blue"
                />
              ))}
              {appliedFilters.suppliers?.map((sup) => (
                <FilterChip
                  key={sup}
                  label={sup}
                  onRemove={() => removeFilterChip('suppliers', sup)}
                  color="purple"
                />
              ))}
              {appliedFilters.materials?.map((mat) => (
                <FilterChip
                  key={mat}
                  label={mat}
                  onRemove={() => removeFilterChip('materials', mat)}
                  color="green"
                />
              ))}
              {appliedFilters.locations?.map((loc) => (
                <FilterChip
                  key={loc}
                  label={loc}
                  onRemove={() => removeFilterChip('locations', loc)}
                  color="orange"
                />
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </button>

            {showSaveDialog ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSaveView()}
                  placeholder="Enter view name..."
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleSaveView}
                  disabled={savingView}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50"
                >
                  {savingView ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSaveDialog(true)}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Current View
              </button>
            )}
          </div>

          <button
            onClick={handleApplyFilters}
            className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-sm"
          >
            <Filter className="h-4 w-4 mr-2" />
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

// Filter Chip Component
const FilterChip = ({ label, onRemove, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    purple: 'bg-purple-100 text-purple-800 border-purple-300',
    green: 'bg-green-100 text-green-800 border-green-300',
    orange: 'bg-orange-100 text-orange-800 border-orange-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${colors[color]}`}
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:bg-black/10 rounded-full p-0.5 transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
};

export default SliceAndDicePanel;
